import React, { useEffect, useState } from "react";
import { Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Button } from "@mui/material";
import { getUserById, updateUser, PHONE_ALIASES } from "../../services/dataService";
import { logUserActivity } from "../../utils/activityLogger";

const phoneOf = (u) => PHONE_ALIASES.map((f) => u?.[f]).find(Boolean) || "";

// Name · phone · gender (044 D23) — the three fields the list's Edit offered, now one
// dialog shared by the list and the student page (D29). Saves through updateUser and
// writes the same UPDATE_STUDENT activity row the list wrote.
const EditStudentDialog = ({ open, onClose, student, actingUser, onSaved }) => {
  const [form, setForm] = useState({ name: "", phone: "", gender: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ name: student?.name || "", phone: phoneOf(student), gender: student?.gender || "" });
      setError("");
    }
  }, [open, student]);

  const save = async () => {
    if (!student?.id) return;
    try {
      setSaving(true);
      setError("");
      const oldData = await getUserById(student.id);
      const before = oldData
        ? { name: oldData.name || "", phone: phoneOf(oldData), gender: oldData.gender || "" }
        : {};
      await updateUser(student.id, form);
      await logUserActivity({
        userId: actingUser?.uid || actingUser?.id || "unknown",
        userName: actingUser?.name || actingUser?.displayName || "Unknown User",
        userEmail: actingUser?.email || "unknown@example.com",
        action: "UPDATE_STUDENT",
        entityType: "student",
        entityId: student.id,
        entityName: form.name || "Unknown Student",
        before,
        after: form,
        description: `${actingUser?.name || "User"} updated student ${form.name || student.id}`,
      });
      onClose();
      onSaved();
    } catch (e) {
      console.error("Error saving student:", e);
      setError(e.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = { borderRadius: "8px", "& .MuiInputBase-root": { borderRadius: "6px" } };
  const buttonSx = { padding: "8px 32px", fontSize: 15, fontWeight: 500 };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "8px" } }}
    >
      <DialogTitle style={{ fontWeight: 600 }}>Edit Student</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <TextField
          label="Name"
          fullWidth
          margin="normal"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          sx={fieldSx}
        />
        <TextField
          label="Phone"
          fullWidth
          margin="normal"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          sx={fieldSx}
        />
        <TextField
          select
          label="Gender"
          fullWidth
          margin="normal"
          value={form.gender}
          onChange={(e) => setForm({ ...form, gender: e.target.value })}
          sx={fieldSx}
        >
          <MenuItem value="male">Male</MenuItem>
          <MenuItem value="female">Female</MenuItem>
          <MenuItem value="other">Other</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={buttonSx}>
          Cancel
        </Button>
        <Button onClick={save} variant="contained" disabled={saving} sx={buttonSx}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditStudentDialog;
